import { MixedSchema } from '@principle-theorem/editor';
import {
  IBrand,
  IPractice,
  IPracticeSetupStep,
  PracticeSetupStepCategory,
  PracticeSetupStepId,
} from '@principle-theorem/principle-core/interfaces';
import { Region, WithRef } from '@principle-theorem/shared';
import { Observable } from 'rxjs';

/**
 * Represents the numerator/denominator progress of a setup step's validation.
 *
 * Returned as part of `IValidationResult` from a step's `validate$` function.
 * Used by the setup step banner to display progress (e.g., "3/7 completed").
 * When `pageVisitTracking` is configured, `current` and `total` can override
 * the visit-based counts as the progress source.
 */
export interface IValidationProgress {
  /**
   * How many items/routes/entities have been satisfied so far.
   */
  current: number;

  /**
   * The total required for the step to be considered complete.
   */
  total: number;
}

/**
 * The result returned by a step's `validate$` function, describing whether
 * the step's requirements are satisfied.
 */
export interface IValidationResult {
  /**
   * Whether the step passes validation.
   */
  valid: boolean;

  /**
   * A human-readable explanation when validation fails (e.g., "At least 3
   * treatments required"). Displayed as a validation error in the UI.
   */
  reason?: string;

  /**
   * Optional numerator/denominator progress (e.g., 3/7 entities
   *   configured). Used by the setup step banner to display a progress indicator,
   *   and can override page-visit-based counts when `pageVisitTracking` is configured.
   */
  progress?: IValidationProgress;
}

/**
 * Optional overrides passed to a step's `validate$` function that allow
 * bypassing normal validation logic.
 *
 * Most steps treat both flags identically (either bypasses validation),
 * though some steps (e.g., conditions setup) only honour `reviewed`.
 */
export interface IStepValidationOptions {
  /**
   * When true, the step is considered valid regardless of its actual
   * validation state. Used when a user has manually reviewed the step.
   */
  reviewed?: boolean;

  /**
   * When true, the step is also considered valid. Used when a user
   * has explicitly chosen to skip the step.
   */
  skipped?: boolean;
}

/**
 * Controls how page visits are tracked and translated into progress for a setup step.
 * If omitted from a step definition, no visits are recorded and progress relies solely on `validate$`.
 */
export interface IPageVisitTrackingConfig {
  /**
   * URL path prefixes that count as a "visit". Matching is segment-based
   * with dynamic `{param}` segments stripped.
   */
  routes: string[];

  /**
   * How visits determine progress:
   * - `'any'`: No progress bar. At least one matching route visit is sufficient.
   * - `'all'`: Progress = unique routes visited / total configured routes. All must be visited.
   * - `'count'`: Progress = unique routes visited / `requiredCount`. Only a subset need visiting.
   */
  validationMode: 'any' | 'all' | 'count';

  /**
   * The progress denominator for `'count'` mode (e.g., 3 → "2/3 visited")
   */
  requiredCount?: number;
}

export interface IPracticeSetupStepDefinition {
  id: PracticeSetupStepId;
  category: PracticeSetupStepCategory;
  title: string;
  description: MixedSchema;
  dependsOn: PracticeSetupStepId[];

  /**
   * Returns the Angular route path for this step given the current brand and practice.
   * Used to map URLs to steps for banner matching and navigation.
   */
  getRoute: (brand: WithRef<IBrand>, practice: WithRef<IPractice>) => string;

  /**
   * Observable that emits an `IValidationResult` representing whether
   * the step's requirements are met. Accepts optional `IStepValidationOptions` to
   * allow reviewed/skipped overrides. Re-evaluated on each page visit when tracking is enabled.
   */
  validate$: (
    practice$: Observable<WithRef<IPractice>>,
    step?: IPracticeSetupStep,
    options?: IStepValidationOptions
  ) => Observable<IValidationResult>;

  /**
   * Optional config controlling automatic visit-based progress
   * tracking. When omitted, the step relies solely on `validate$` for completion.
   */
  pageVisitTracking?: IPageVisitTrackingConfig;

  /**
   * Optional region filter. When set, the step is only included in wizards
   * for matching regions (e.g., AU-only or NZ-only steps)
   */
  regions?: Region[];
}
